import { useState, useEffect } from "react";
import { Outlet, NavLink, Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { userLogout } from "../../store/slices/authSlice";
import {
  User,
  FileText,
  Bell,
  Heart,
  Settings,
  LogOut,
  Home,
  Menu,
  X,
  ChevronRight,
  Sparkles,
  Shield,
  Award,
  Loader2,
  Mail,
  Clock,
  RefreshCw,
  AlertCircle,
  CheckCircle,
} from "lucide-react";
import ThemeToggle from "../../components/common/ThemeToggle";
import { motion, AnimatePresence } from "framer-motion";
import { requestService } from "../../api/services/requestService";
import { notificationService } from "../../api/services/notificationService";
import { userService } from "../../api/services/userService";
import { useWishlist } from "../../context/WishlistContext";
import { useToast } from "../../components/common/ThemeToaster";

const UserLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user, isAuthenticated, isAdmin } = useSelector((state) => state.auth);
  const { getWishlistCount } = useWishlist();
  const { success: toastSuccess, error: toastError } = useToast();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : false;
  });
  const [stats, setStats] = useState({
    requestsCount: 0,
    notificationsCount: 0,
    wishlistCount: 0,
  });
  const [loadingStats, setLoadingStats] = useState(true);
  const [isResendingVerification, setIsResendingVerification] = useState(false);

  // Apply dark mode class
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoadingStats(true);

        let requestsCount = 0;
        try {
          const requestsResponse = await requestService.getMyRequests({
            limit: 1,
          });
          if (requestsResponse?.pagination?.total) {
            requestsCount = requestsResponse.pagination.total;
          } else if (requestsResponse?.data?.pagination?.total) {
            requestsCount = requestsResponse.data.pagination.total;
          } else if (Array.isArray(requestsResponse?.requests)) {
            requestsCount = requestsResponse.requests.length;
          } else if (Array.isArray(requestsResponse?.data?.requests)) {
            requestsCount = requestsResponse.data.requests.length;
          }
        } catch (err) {
          console.error("Error fetching requests count:", err);
        }

        let notificationsCount = 0;
        try {
          const notificationsResponse =
            await notificationService.getUnreadCount();
          if (notificationsResponse?.data?.unreadCount !== undefined) {
            notificationsCount = notificationsResponse.data.unreadCount;
          } else if (notificationsResponse?.unreadCount !== undefined) {
            notificationsCount = notificationsResponse.unreadCount;
          }
        } catch (err) {
          console.error("Error fetching notifications count:", err);
        }

        const wishlistCount = getWishlistCount();

        setStats({
          requestsCount,
          notificationsCount,
          wishlistCount,
        });
      } catch (err) {
        console.error("Error fetching stats:", err);
      } finally {
        setLoadingStats(false);
      }
    };

    fetchStats();
  }, [getWishlistCount]);

  const handleResendVerification = async () => {
    setIsResendingVerification(true);
    try {
      await userService.resendVerificationEmail();
      toastSuccess("Verification email sent successfully!");
    } catch (err) {
      console.error("Error resending verification:", err);
      toastError(
        err?.response?.data?.message || "Failed to resend verification email",
      );
    } finally {
      setIsResendingVerification(false);
    }
  };

  // Redirect if not authenticated or if admin is logged in
  if (!isAuthenticated || isAdmin) {
    return <Navigate to="/user/login" replace />;
  }

  // Show loading if no user data
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white dark:from-gray-950 dark:to-gray-900">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full blur-xl opacity-50 animate-pulse" />
            <div className="relative w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          </div>
          <p className="text-gray-600 dark:text-gray-400 font-medium">
            Loading your account...
          </p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    dispatch(userLogout());
  };

  const navItems = [
    {
      to: "/user/profile",
      icon: User,
      label: "Profile",
      description: "View and edit your profile",
    },
    {
      to: "/user/requests",
      icon: FileText,
      label: "My Requests",
      description: "Track your product requests",
      badge: stats.requestsCount > 0 ? stats.requestsCount : null,
    },
    {
      to: "/user/notifications",
      icon: Bell,
      label: "Notifications",
      description: "Stay updated",
      badge: stats.notificationsCount > 0 ? stats.notificationsCount : null,
    },
    {
      to: "/user/wishlist",
      icon: Heart,
      label: "Wishlist",
      description: "Saved products",
      badge: stats.wishlistCount > 0 ? stats.wishlistCount : null,
    },
    {
      to: "/user/settings",
      icon: Settings,
      label: "Settings",
      description: "Manage preferences",
    },
  ];

  const getPageTitle = () => {
    const currentItem = navItems.find((item) =>
      location.pathname.includes(item.to),
    );
    return currentItem?.label || "Dashboard";
  };

  // Check if email is not verified
  const isEmailVerified =
    user.emailVerifiedAt !== null && user.emailVerifiedAt !== undefined;

  // Email Verification Banner Component
  const EmailVerificationBanner = () => (
    <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
      <div className="flex items-start gap-3">
        <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex-shrink-0">
          <Mail size={20} className="text-yellow-600 dark:text-yellow-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
            Verify Your Email Address
          </h3>
          <p className="text-xs text-gray-600 dark:text-gray-400 mb-2">
            We've sent a verification email to{" "}
            <span className="font-medium text-gray-900 dark:text-white">
              {user.email}
            </span>
            . Please check your inbox and click the verification link to
            activate your account.
          </p>
          <button
            onClick={handleResendVerification}
            disabled={isResendingVerification}
            className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-500 hover:bg-yellow-600 text-white text-xs font-medium rounded-lg transition-all disabled:opacity-50"
          >
            {isResendingVerification ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Resend Verification Email
          </button>
        </div>
      </div>
    </div>
  );

  // Full Email Verification Overlay for unverified users
  if (!isEmailVerified) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 flex items-center justify-center py-12 px-4">
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-yellow-200/20 to-orange-200/20 dark:from-yellow-900/20 dark:to-orange-900/20 rounded-full blur-3xl animate-pulse" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full relative z-10"
        >
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-800/50 overflow-hidden">
            {/* Header */}
            <div className="p-6 sm:p-8 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-b border-gray-100 dark:border-gray-800 text-center">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mb-4 shadow-lg">
                <Mail size={32} className="text-white" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Verify Your Email
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                We've sent a verification link to your email
              </p>
            </div>

            {/* Content */}
            <div className="p-6 sm:p-8">
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 mb-6">
                <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                  <span className="font-semibold">{user.email}</span>
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <AlertCircle
                    size={14}
                    className="text-yellow-500 flex-shrink-0"
                  />
                  <span>Check your inbox and spam folder</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Clock size={14} className="text-yellow-500 flex-shrink-0" />
                  <span>Verification link expires in 24 hours</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <Shield size={14} className="text-yellow-500 flex-shrink-0" />
                  <span>Required to access all features</span>
                </div>
              </div>

              <button
                onClick={handleResendVerification}
                disabled={isResendingVerification}
                className="w-full py-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isResendingVerification ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <RefreshCw size={18} />
                )}
                Resend Verification Email
              </button>

              <button
                onClick={handleLogout}
                className="w-full mt-3 py-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-medium rounded-xl transition-all hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center gap-2"
              >
                <LogOut size={18} />
                Logout
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      {/* Animated Background Blobs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      {/* Header */}
      <header
        className={`sticky top-0 z-40 transition-all duration-300 ${
          scrolled
            ? "bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl shadow-lg border-b border-gray-200/50 dark:border-gray-800/50"
            : "bg-white/60 dark:bg-gray-900/60 backdrop-blur-md border-b border-gray-100 dark:border-gray-800"
        }`}
      >
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-6">
              <NavLink
                to="/"
                className="flex items-center gap-2 text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors group"
              >
                <div className="p-1.5 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 group-hover:shadow-lg transition-all">
                  <Home size={16} className="text-white" />
                </div>
                <span className="text-sm font-medium hidden sm:inline">
                  Home
                </span>
              </NavLink>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-700 hidden sm:block" />
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                  <User size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                    My Account
                  </p>
                  <h1 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white">
                    {getPageTitle()}
                  </h1>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-4">
              <ThemeToggle darkMode={darkMode} setDarkMode={setDarkMode} />

              <div className="hidden sm:flex items-center gap-3 px-3 py-1.5 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
                  <span className="text-xs font-bold text-white">
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </span>
                </div>
                <div className="text-left">
                  <p className="text-xs font-medium text-gray-900 dark:text-white">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-[10px] text-gray-500 dark:text-gray-400">
                    {user.email}
                  </p>
                </div>
              </div>

              <button
                onClick={handleLogout}
                className="p-2 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 group"
                title="Logout"
              >
                <LogOut
                  size={20}
                  className="group-hover:scale-110 transition-transform"
                />
              </button>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50"
              >
                {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.aside
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 z-50 w-80 h-full bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl md:hidden"
          >
            <div className="p-6 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                  <span className="text-lg font-bold text-white">
                    {user.firstName?.charAt(0)}
                    {user.lastName?.charAt(0)}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {user.firstName} {user.lastName}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <Sparkles size={12} className="text-primary-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Member since {new Date(user.createdAt).getFullYear()}
                  </span>
                </div>
              </div>
            </div>

            <nav className="p-4 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname.includes(item.to);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === "/user/profile"}
                    className={({ isActive: active }) =>
                      `flex items-center justify-between p-3 rounded-xl transition-all ${
                        active
                          ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20"
                          : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`
                    }
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-lg ${isActive ? "bg-primary-100 dark:bg-primary-900/20" : ""}`}
                      >
                        <Icon size={18} />
                      </div>
                      <div>
                        <p className="font-medium">{item.label}</p>
                        <p className="text-xs text-gray-500">
                          {item.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {item.badge && (
                        <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                          {item.badge > 99 ? "99+" : item.badge}
                        </span>
                      )}
                      <ChevronRight
                        size={16}
                        className={`transition-transform ${isActive ? "translate-x-1" : ""}`}
                      />
                    </div>
                  </NavLink>
                );
              })}
            </nav>

            <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-gray-100 dark:border-gray-800">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-100 dark:hover:bg-red-900/30 transition-all"
              >
                <LogOut size={18} />
                Sign Out
              </button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10 relative z-10">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar - Desktop */}
          <aside className="hidden lg:block lg:w-72 flex-shrink-0">
            <div className="sticky top-24">
              {/* User Card */}
              <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg p-5 mb-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-lg">
                    <span className="text-xl font-bold text-white">
                      {user.firstName?.charAt(0)}
                      {user.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {user.email}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Shield size={10} className="text-green-500" />
                      <span className="text-[10px] text-gray-500">
                        Verified Account
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {loadingStats ? (
                        <Loader2 size={16} className="animate-spin mx-auto" />
                      ) : (
                        stats.requestsCount
                      )}
                    </p>
                    <p className="text-[10px] text-gray-500">Requests</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {loadingStats ? (
                        <Loader2 size={16} className="animate-spin mx-auto" />
                      ) : (
                        stats.notificationsCount
                      )}
                    </p>
                    <p className="text-[10px] text-gray-500">Notifications</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-lg font-bold text-gray-900 dark:text-white">
                      {loadingStats ? (
                        <Loader2 size={16} className="animate-spin mx-auto" />
                      ) : (
                        stats.wishlistCount
                      )}
                    </p>
                    <p className="text-[10px] text-gray-500">Wishlist</p>
                  </div>
                </div>
              </div>

              {/* Navigation */}
              <nav className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg overflow-hidden">
                <div className="p-4 space-y-1">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname.includes(item.to);
                    return (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.to === "/user/profile"}
                        className={({ isActive: active }) =>
                          `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                            active
                              ? "bg-gradient-to-r from-primary-500/10 to-secondary-500/10 text-primary-600 dark:text-primary-400 border border-primary-500/20"
                              : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/50"
                          }`
                        }
                      >
                        <Icon size={18} />
                        <span className="font-medium">{item.label}</span>
                        <div className="ml-auto flex items-center gap-2">
                          {item.badge && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                              {item.badge > 99 ? "99+" : item.badge}
                            </span>
                          )}
                          {location.pathname.includes(item.to) && (
                            <div className="w-1.5 h-1.5 rounded-full bg-primary-500" />
                          )}
                        </div>
                      </NavLink>
                    );
                  })}
                </div>
              </nav>

              {/* Trust Badge */}
              <div className="mt-6 p-4 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-2xl border border-primary-200/30 dark:border-primary-800/30">
                <div className="flex items-center gap-2 mb-2">
                  <Award size={16} className="text-primary-500" />
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    Trusted Platform
                  </p>
                </div>
                <p className="text-[10px] text-gray-600 dark:text-gray-400">
                  Your data is protected with enterprise-grade security
                </p>
              </div>
            </div>
          </aside>

          {/* Content Area */}
          <main className="flex-1 min-w-0">
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-2xl border border-gray-200/50 dark:border-gray-800/50 shadow-lg overflow-hidden">
              <div className="p-4 sm:p-6 lg:p-8">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserLayout;
